<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { aiService, type AIProjectionData } from '@/api/aiService'

const projectionData = ref<AIProjectionData | null>(null)
const loading = ref(true)
const selectedLang = ref<'sw' | 'en'>('sw')
const showDetails = ref(false)

const fetchProjections = async () => {
  loading.value = true
  try {
    const data = await aiService.getFarmProjections(selectedLang.value)
    projectionData.value = data
  } catch (err) {
    console.error('Failed to load AI projections:', err)
  } finally {
    loading.value = false
  }
}

const toggleLang = (lang: 'sw' | 'en') => {
  selectedLang.value = lang
  fetchProjections()
}

onMounted(fetchProjections)
</script>

<template>
  <div class="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 shadow-xl border border-indigo-500/20 overflow-hidden font-sans">
    <!-- Glowing Background Highlights -->
    <div class="absolute -top-12 -right-12 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute -bottom-12 -left-12 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

    <!-- Header & Language Controls -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl shadow-lg shadow-indigo-500/30">
          🔮
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-base font-bold text-white tracking-wide">
              {{ projectionData?.title || (selectedLang === 'sw' ? 'Utabiri na Mkakati wa Kukuza Faida (AI Projections)' : 'AI Profit & Yield Projections') }}
            </h2>
            <span
              v-if="projectionData?.is_data_sufficient"
              class="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              {{ selectedLang === 'sw' ? 'Data Halisi' : 'Live Data' }}
            </span>
            <span
              v-else-if="projectionData && !projectionData.is_data_sufficient"
              class="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
              {{ selectedLang === 'sw' ? 'Inasubiri Data' : 'Awaiting Logs' }}
            </span>
          </div>
          <p class="text-xs text-indigo-200/80 mt-0.5">
            {{ projectionData?.subtitle || (selectedLang === 'sw' ? 'Tathmini ya uzalishaji halisi na urari wa fedha' : '30-Day predictive farm analytics based on live database records') }}
          </p>
        </div>
      </div>

      <!-- Controls -->
      <div class="flex items-center gap-2">
        <div class="flex items-center bg-slate-800/80 p-0.5 rounded-xl border border-slate-700">
          <button
            @click="toggleLang('sw')"
            class="px-3 py-1 text-xs font-bold rounded-lg transition-all"
            :class="selectedLang === 'sw' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'"
          >
            Kiswahili
          </button>
          <button
            @click="toggleLang('en')"
            class="px-3 py-1 text-xs font-bold rounded-lg transition-all"
            :class="selectedLang === 'en' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'"
          >
            English
          </button>
        </div>

        <button
          @click="fetchProjections"
          :disabled="loading"
          class="p-2 bg-slate-800/80 text-slate-300 hover:text-white rounded-xl border border-slate-700 transition-colors disabled:opacity-50"
          title="Refresh AI Predictions"
        >
          <svg :class="loading ? 'animate-spin' : ''" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="loading" class="py-12 flex items-center justify-center gap-3 text-indigo-300 text-sm font-medium">
      <div class="animate-spin h-6 w-6 border-2 border-indigo-400 border-t-transparent rounded-full"></div>
      {{ selectedLang === 'sw' ? 'Inachambua kumbukumbu za uzalishaji, lishe na fedha...' : 'Analyzing live farm yield, feed logs & financial trends...' }}
    </div>

    <!-- Content -->
    <div v-else-if="projectionData" class="space-y-6 relative z-10">
      <!-- Top Metrics Highlights Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <!-- Metric 1: Projected Production Volume -->
        <div class="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between">
              <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                {{ selectedLang === 'sw' ? 'Makadirio ya Uzalishaji (30d)' : 'Forecasted Yield (30 Days)' }}
              </p>
              <span class="text-[10px] text-slate-500 font-medium">
                {{ projectionData.metrics.primary_unit || 'Units' }}
              </span>
            </div>
            <div class="flex items-baseline gap-2 mt-2">
              <span class="text-2xl font-black text-white">
                {{ projectionData.metrics.projected_30_yield.toLocaleString() }}
              </span>
              <span class="text-xs font-bold text-slate-300">
                {{ projectionData.metrics.primary_unit || 'Units' }}
              </span>
            </div>
            <!-- Commodity breakdown pills -->
            <div v-if="projectionData.commodities && projectionData.commodities.length > 0" class="mt-2.5 flex flex-wrap gap-1.5">
              <span
                v-for="(c, idx) in projectionData.commodities.slice(0, 3)"
                :key="idx"
                class="text-[10px] px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-slate-300 font-medium"
              >
                {{ c.name }}: <strong class="text-white">{{ c.projected_30d.toLocaleString() }}</strong> {{ c.unit }}
              </span>
            </div>
          </div>
          <div class="mt-3 flex items-center justify-between text-xs">
            <div
              class="flex items-center gap-1 font-bold"
              :class="projectionData.metrics.yield_growth_trend >= 0 ? 'text-emerald-400' : 'text-rose-400'"
            >
              <span>{{ projectionData.metrics.yield_growth_trend >= 0 ? '↗' : '↘' }} {{ projectionData.metrics.yield_growth_trend > 0 ? '+' : '' }}{{ projectionData.metrics.yield_growth_trend }}%</span>
              <span class="text-[10px] text-slate-400 font-normal">vs previous 30d</span>
            </div>
            <span class="text-[10px] text-slate-400">
              Current: {{ projectionData.metrics.current_30_production.toLocaleString() }}
            </span>
          </div>
        </div>

        <!-- Metric 2: Net Profit Projection -->
        <div class="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between">
              <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                {{ selectedLang === 'sw' ? 'Makadirio ya Faida Safi' : 'Projected Net Margin' }}
              </p>
              <span class="text-[10px] text-slate-500 font-medium">TSH</span>
            </div>
            <div class="flex items-baseline gap-1 mt-2">
              <span class="text-xs text-slate-400 font-semibold">TSH</span>
              <span
                class="text-2xl font-black"
                :class="projectionData.metrics.projected_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'"
              >
                {{ projectionData.metrics.projected_profit.toLocaleString() }}
              </span>
            </div>
            <div class="mt-2 text-[11px] text-slate-400 flex items-center justify-between">
              <span>Rev: TSH {{ projectionData.metrics.projected_revenue.toLocaleString() }}</span>
              <span class="text-slate-500">Run-Rate</span>
            </div>
          </div>
          <div class="mt-3 flex items-center gap-1.5 text-xs font-bold text-indigo-300">
            <span>📊 {{ projectionData.metrics.projected_margin_pct }}% Net Margin</span>
          </div>
        </div>

        <!-- Metric 3: Feed Efficiency Index (FCI & FCR) -->
        <div class="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between">
              <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                {{ selectedLang === 'sw' ? 'Ufanisi wa Lishe (FCI)' : 'Feed Conversion Index' }}
              </p>
              <span
                v-if="projectionData.feed_metrics?.fcr_ratio"
                class="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30"
              >
                FCR: {{ projectionData.feed_metrics.fcr_ratio }} kg/u
              </span>
            </div>
            <div class="flex items-baseline gap-2 mt-2">
              <span class="text-2xl font-black text-amber-400">
                {{ projectionData.metrics.feed_efficiency_score }}%
              </span>
              <span class="text-xs font-semibold text-slate-400">Score</span>
            </div>
            <div class="mt-2 text-[11px] text-slate-400 truncate">
              <span v-if="projectionData.feed_metrics?.total_feed_consumed_kg">
                {{ projectionData.feed_metrics.total_feed_consumed_kg.toLocaleString() }} kg fed • TSH {{ projectionData.feed_metrics.total_feed_cost.toLocaleString() }}
              </span>
              <span v-else class="text-slate-500">
                {{ selectedLang === 'sw' ? 'Hakuna kumbukumbu za lishe' : 'Awaiting feeding records' }}
              </span>
            </div>
          </div>
          <div class="mt-3 w-full bg-slate-800 h-2 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-amber-500 to-emerald-400 rounded-full transition-all duration-500"
              :style="{ width: `${Math.min(100, projectionData.metrics.feed_efficiency_score)}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Detailed Breakdown Toggle -->
      <div class="flex items-center justify-between pt-1">
        <button
          @click="showDetails = !showDetails"
          class="text-xs text-indigo-300 hover:text-indigo-100 font-semibold flex items-center gap-1.5 transition-colors"
        >
          <span>{{ showDetails ? '▲' : '▼' }}</span>
          <span>{{ showDetails ? (selectedLang === 'sw' ? 'Ficha Uchanganuzi wa Kina' : 'Hide Detailed Breakdown') : (selectedLang === 'sw' ? 'Angalia Uchanganuzi wa Kina (Mazao, Lishe, Fedha)' : 'View Detailed Breakdown (Commodities, Feed, Run-rates)') }}</span>
        </button>
      </div>

      <!-- Collapsible Detailed Breakdown Drawer -->
      <div v-if="showDetails" class="bg-slate-900/90 border border-indigo-500/30 rounded-2xl p-5 space-y-4">
        <!-- Section A: Commodity Breakdown Table -->
        <div v-if="projectionData.commodities && projectionData.commodities.length > 0">
          <h4 class="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
            {{ selectedLang === 'sw' ? '📊 Uzalishaji kwa Kila Bidhaa/Zao' : '📊 Production Breakdown by Commodity' }}
          </h4>
          <div class="overflow-x-auto">
            <table class="w-full text-xs text-left border-collapse">
              <thead>
                <tr class="border-b border-slate-800 text-slate-400 font-semibold">
                  <th class="py-2 pr-4">Commodity</th>
                  <th class="py-2 px-3">Type</th>
                  <th class="py-2 px-3">Past 30d</th>
                  <th class="py-2 px-3">Daily Run-Rate</th>
                  <th class="py-2 px-3 text-emerald-400">Projected 30d</th>
                  <th class="py-2 pl-3">Trend</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-800/60 text-slate-200">
                <tr v-for="(c, i) in projectionData.commodities" :key="i" class="hover:bg-slate-800/40">
                  <td class="py-2 pr-4 font-bold text-white">{{ c.name }}</td>
                  <td class="py-2 px-3 text-slate-400">{{ c.category }}</td>
                  <td class="py-2 px-3">{{ c.current_30d.toLocaleString() }} {{ c.unit }}</td>
                  <td class="py-2 px-3">{{ c.daily_average.toLocaleString() }} {{ c.unit }}/day</td>
                  <td class="py-2 px-3 font-black text-emerald-400">{{ c.projected_30d.toLocaleString() }} {{ c.unit }}</td>
                  <td class="py-2 pl-3 font-semibold" :class="c.trend_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'">
                    {{ c.trend_pct > 0 ? '+' : '' }}{{ c.trend_pct }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Section B: Feed & Financial Detailed Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
          <!-- Feed Stats -->
          <div class="bg-slate-800/40 p-3.5 rounded-xl border border-slate-800 text-xs space-y-1.5">
            <p class="font-bold text-amber-300 uppercase tracking-wider text-[10px]">
              {{ selectedLang === 'sw' ? 'Muhtasari wa Lishe & FCR' : 'Feed Intake & FCR Metrics' }}
            </p>
            <div class="flex justify-between text-slate-300">
              <span>Total Feed Distributed:</span>
              <strong class="text-white">{{ projectionData.feed_metrics?.total_feed_consumed_kg || 0 }} kg</strong>
            </div>
            <div class="flex justify-between text-slate-300">
              <span>Feed Cost Logged:</span>
              <strong class="text-white">TSH {{ (projectionData.feed_metrics?.total_feed_cost || 0).toLocaleString() }}</strong>
            </div>
            <div class="flex justify-between text-slate-300">
              <span>Avg Feed/Animal/Day:</span>
              <strong class="text-white">{{ projectionData.feed_metrics?.avg_daily_feed_per_animal_kg || 0 }} kg</strong>
            </div>
            <div class="flex justify-between text-slate-300">
              <span>Concentrate / Roughage / Supp:</span>
              <span class="text-slate-400 text-[11px]">{{ projectionData.feed_metrics?.concentrate_kg || 0 }}kg / {{ projectionData.feed_metrics?.roughage_kg || 0 }}kg / {{ projectionData.feed_metrics?.supplement_kg || 0 }}kg</span>
            </div>
          </div>

          <!-- Financial Run-Rate Stats -->
          <div class="bg-slate-800/40 p-3.5 rounded-xl border border-slate-800 text-xs space-y-1.5">
            <p class="font-bold text-emerald-300 uppercase tracking-wider text-[10px]">
              {{ selectedLang === 'sw' ? 'Urari wa Fedha wa Sasa' : 'Current Financial Run-Rate' }}
            </p>
            <div class="flex justify-between text-slate-300">
              <span>30-Day Sales Revenue:</span>
              <strong class="text-white">TSH {{ (projectionData.financial_metrics?.current_revenue || 0).toLocaleString() }}</strong>
            </div>
            <div class="flex justify-between text-slate-300">
              <span>30-Day Operating Costs:</span>
              <strong class="text-white">TSH {{ (projectionData.financial_metrics?.operating_cost || 0).toLocaleString() }}</strong>
            </div>
            <div class="flex justify-between text-slate-300">
              <span>Daily Operating Burn Rate:</span>
              <strong class="text-white">TSH {{ (projectionData.financial_metrics?.daily_burn_rate || 0).toLocaleString() }}/day</strong>
            </div>
            <div class="flex justify-between text-slate-300">
              <span>Daily Sales Rate:</span>
              <strong class="text-white">TSH {{ (projectionData.financial_metrics?.daily_sales_rate || 0).toLocaleString() }}/day</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Strategic Recommendations List -->
      <div class="bg-slate-900/90 border border-indigo-500/20 rounded-2xl p-5">
        <h3 class="text-xs font-bold text-indigo-300 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span>💡</span>
          <span>{{ selectedLang === 'sw' ? 'Mapendekezo ya AI ya Kukuza Uzalishaji na Kupunguza Gharama' : 'AI Profit & Yield Maximization Advice' }}</span>
        </h3>
        <ul class="space-y-2.5">
          <li
            v-for="(rec, idx) in projectionData.recommendations"
            :key="idx"
            class="text-xs text-slate-200 leading-relaxed bg-slate-800/50 p-3 rounded-xl border border-slate-800 flex items-start gap-2.5"
          >
            <span class="text-indigo-400 font-bold flex-shrink-0 mt-0.5">•</span>
            <span v-html="rec.replace(/\*\*(.*?)\*\*/g, '<strong class=\'text-white font-bold\'>$1</strong>')"></span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
